import React from 'react';
type Tome = {
  number: string;
  title: string;
  weeks: string;
  framework: string;
  promise: string;
  badge: string;
  contents: string[];
};
const tomes: Tome[] = [{
  number: '01',
  title: 'Foundations',
  weeks: 'Weeks 1–9',
  framework: 'The 52-Week Barre Map',
  promise: 'Build the base every class you teach relies on.',
  badge: 'bg-sage',
  contents: ['Week 1: a 55-minute class you can teach tomorrow', 'Pliés, chair, fold-over seat, pretzel and mat C-curve', 'Mixed-level day: one class, three intensities', 'Your first class with a new group: 5-minute intro script', 'Barre terms in plain English: 25 words for beginners', 'Demo or walk the room? When to show and when to correct']
}, {
  number: '02',
  title: 'Strength & Holds',
  weeks: 'Weeks 10–18',
  framework: 'The Hold–Pulse–Release Ladder',
  promise: 'Longer holds, cleaner form — hold ladders for every level.',
  badge: 'bg-plum',
  contents: ['Isometric chair, seat and plank holds in three lengths', 'The "last 8" your regulars remember', 'A no-props strength day, bodyweight only', 'Hold-length planner by level', 'Last-minute sub plan: a 45-minute class, zero prep', 'Breath cues that keep the room breathing through long holds']
}, {
  number: '03',
  title: 'Tempo & Endurance',
  weeks: 'Weeks 19–27',
  framework: 'The Tempo Dial',
  promise: 'Same moves, new speed: keep classes fresh with tempo alone.',
  badge: 'bg-coral',
  contents: ['Half time, full time, double time and pulse on any block', 'Tempo pyramids and work–rest intervals', 'Low-impact cardio your whole room can keep up with', 'Playlist planner by BPM — no song titles needed', 'Count scripts for when the speaker fails', 'Transitions under 10 seconds']
}, {
  number: '04',
  title: 'Props & Variety',
  weeks: 'Weeks 28–36',
  framework: 'The Prop Swap Grid',
  promise: 'One block, four ways: ball, band, light weights or nothing at all.',
  badge: 'bg-gold-deep',
  contents: ['Small ball, loop band, light weights and gliders', 'A no-prop version of every block', 'Station circuits for any group size', 'Hand-out and reset routines that save 5 minutes', 'Minimum-equipment kit and household swaps', "What to do when half the room doesn't have the prop"]
}, {
  number: '05',
  title: 'Balance & Center Floor',
  weeks: 'Weeks 37–44',
  framework: 'The Center-Floor Bridge',
  promise: 'Step away from the barre with confidence.',
  badge: 'bg-smoke',
  contents: ['Two hands, one hand, no hands: the balance ladder', 'Relevé walks, arabesque prep and quarter turns', 'Combination day: three moves in one song', 'A full class for rooms without a barre', 'Wall, chair and counter substitutes', 'Spacing and sightlines for small rooms']
}, {
  number: '06',
  title: 'Peak & Reset',
  weeks: 'Weeks 45–52',
  framework: 'The Peak & Reset Cycle',
  promise: 'Peak weeks, a planned deload and classes your regulars ask for again.',
  badge: 'bg-plum-mid',
  contents: ['Signature flows built to be taught on repeat', 'Deload week: lower intensity, same quality', 'Partner and group formats for rooms of 20+', 'The countdown challenge class', 'Cancelled classes and holiday weeks: stay on track', 'Second-cycle guide: teach the year again, higher standard']
}];
export function Tomes() {
  return <section aria-labelledby="tomes-heading" className="w-full bg-cream" data-id="element-273" data-mp="mv4bfqxdgj69">
      <div className="max-w-6xl mx-auto px-5 py-16 sm:py-20" data-id="element-274" data-mp="rnnqytfmdodp">
        <div className="text-center" data-id="element-275" data-mp="3ravops6bkt5x">
          <p className="text-[11px] font-semibold uppercase tracking-[1.8px] text-gold-deep" data-id="element-276" data-mp="3r9ucvlhhc2n5">
            The complete year
          </p>
          <h2 id="tomes-heading" className="mt-3 font-display font-semibold text-plum text-3xl sm:text-5xl" data-id="element-277" data-mp="3vae8ulqbh07p">
            Six volumes. <span className="italic" data-id="element-278" data-mp="379u3gccwryxr">Fifty-two weeks.</span>
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base text-ink-soft" data-id="element-279" data-mp="3lxf7c8hly26q">
            Each volume covers about two months and works on its own: its
            warm-ups, cooldowns and blocks are all inside it.
          </p>
        </div>

        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-280" data-mp="3ux26enmv6vwm">
          {tomes.map(tome => <article key={tome.number} className="flex flex-col rounded-3xl border border-cream-dark bg-white p-6 shadow-card" data-id="element-281" data-mp="3pyqwkcwkklee">
              <div className="flex items-center gap-2.5" data-id="element-282" data-mp="3rsqjosl3y0q">
                <span className={`flex h-9 w-9 items-center justify-center rounded-full font-display text-base font-semibold text-white ${tome.badge}`} data-id="element-283" data-mp="3q7vly2cyd2c">
                  {tome.number}
                </span>
                <span className="text-[11px] font-semibold uppercase tracking-widest text-ink-muted" data-id="element-284" data-mp="l14s5g0e16n2">
                  Volume {tome.number} · {tome.weeks}
                </span>
              </div>
              <h3 className="mt-3 font-display text-2xl font-semibold leading-snug text-plum" data-id="element-285" data-mp="bcczf5ni6ib3">
                {tome.title}
              </h3>
              <p className="mt-1 text-[12px] font-semibold uppercase tracking-wider text-gold-deep" data-id="element-286" data-mp="g8ld0em1y3g5">
                {tome.framework}
              </p>
              <p className="mt-2 text-sm text-ink-soft" data-id="element-287" data-mp="6cqo51r8jc4x">{tome.promise}</p>
              <ul className="mt-4 grid gap-2 border-t border-cream-dark pt-4" data-id="element-288" data-mp="3oid4xlg9dgax">
                {tome.contents.map(item => <li key={item} className="flex gap-2 text-[13px] text-ink" data-id="element-289" data-mp="3ti99pmws5brj">
                    <span className="mt-[7px] h-1.5 w-1.5 flex-none rounded-full bg-gold" data-id="element-290" data-mp="1mq8ham5sl560" />
                    {item}
                  </li>)}
              </ul>
            </article>)}
        </div>
      </div>
    </section>;
}